import { useState, useEffect, useRef, useCallback } from 'react';
import { useAccount, useReconnect } from 'wagmi';
import { isPWAStandalone } from '../config/wagmi';

const PWA_CONNECTING_KEY = 'pwa_connecting';
const RECONNECT_TIMEOUT_MS = 8000;
const FLAG_MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Call this BEFORE opening the wallet connect modal in PWA standalone mode.
 * Sets a localStorage flag so we know a connection attempt was in progress
 * if iOS kills the PWA in the background.
 */
export function setPWAConnecting() {
  if (isPWAStandalone) {
    localStorage.setItem(PWA_CONNECTING_KEY, String(Date.now()));
  }
}

export function clearPWAConnecting() {
  localStorage.removeItem(PWA_CONNECTING_KEY);
}

/**
 * Hook for iOS PWA wallet reconnection.
 *
 * When an installed PWA switches to MetaMask (via WalletConnect deep link),
 * iOS kills the PWA in the background. On return, React state is wiped.
 * This hook detects the `pwa_connecting` flag and:
 *   1. Shows a "Reconnecting…" state instead of "Connect your wallet"
 *   2. Triggers wagmi's reconnect() to restore the WalletConnect session
 *   3. Falls back to showing the connect modal after a timeout
 *
 * @param {Function} openConnectModal - RainbowKit's openConnectModal (optional)
 * @returns {{ isReconnecting: boolean, isPWA: boolean }}
 */
export function usePWAReconnect(openConnectModal) {
  const { isConnected } = useAccount();
  const { reconnect } = useReconnect();
  const [isReconnecting, setIsReconnecting] = useState(false);
  const timeoutRef = useRef(null);
  const hasTriedRef = useRef(false);

  // On mount: detect the flag and attempt reconnect
  useEffect(() => {
    if (!isPWAStandalone || hasTriedRef.current) return;

    const timestamp = localStorage.getItem(PWA_CONNECTING_KEY);
    if (!timestamp) return;

    // If already connected, just clear
    if (isConnected) {
      clearPWAConnecting();
      return;
    }

    // Ignore stale flags (> 5 min old)
    const elapsed = Date.now() - parseInt(timestamp, 10);
    if (elapsed > FLAG_MAX_AGE_MS) {
      clearPWAConnecting();
      return;
    }

    hasTriedRef.current = true;
    setIsReconnecting(true);

    // Trigger wagmi reconnect (picks up persisted WC sessions)
    try {
      reconnect();
    } catch (e) {
      console.warn('[PWA Reconnect] reconnect() failed:', e);
    }

    // Timeout: if still not connected, stop spinner and optionally open modal
    timeoutRef.current = setTimeout(() => {
      setIsReconnecting(false);
      clearPWAConnecting();
      // Auto-open the connect modal so user can retry with one tap
      if (openConnectModal) {
        try { openConnectModal(); } catch {}
      }
    }, RECONNECT_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount only

  // When connection succeeds, clear everything
  useEffect(() => {
    if (isConnected && isReconnecting) {
      setIsReconnecting(false);
      clearPWAConnecting();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isConnected, isReconnecting]);

  // Wrapper for connect actions — sets the PWA flag before opening modal
  const connectWithFlag = useCallback((connectFn) => {
    setPWAConnecting();
    if (connectFn) connectFn();
  }, []);

  return { isReconnecting, isPWA: isPWAStandalone, connectWithFlag };
}
